import { API_URL, AUTH_TOKEN_KEY } from "../config";
import { parseErrorMessage } from "../utils/errorParser";

// Store recent request timestamps to prevent duplicates
const recentRequests = new Map<string, number>();
const DEBOUNCE_TIME = 600; // Increase from 300ms to 600ms

// Track request counts to detect infinite loops
const requestCountByEndpoint = new Map<string, number>();
const RESET_INTERVAL = 60000; // FIXED: Increased from 10 seconds to 1 minute
const MAX_REQUESTS_PER_ENDPOINT = 15; // Allow up to 15 requests to the same endpoint within RESET_INTERVAL

// Cache for GET requests to prevent unnecessary duplicate calls
const responseCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 60000; // Cache lifetime of 60 seconds for most endpoints
const USERS_CACHE_TTL = 15000; // Shorter cache time for users endpoint (15 seconds)

// Periodically reset the request counter
setInterval(() => {
  requestCountByEndpoint.clear();
}, RESET_INTERVAL);

// Periodically clean up cache to avoid memory leaks (FIXED: Reduced frequency)
setInterval(() => {
  const now = Date.now();
  responseCache.forEach((value, key) => {
    // Determine cache TTL based on endpoint
    const ttl = key.includes("/users") ? USERS_CACHE_TTL : CACHE_TTL;
    if (now - value.timestamp > ttl) {
      responseCache.delete(key);
    }
  });
}, 300000); // FIXED: Changed from 30 seconds to 5 minutes

// Custom error class for API errors
export class ApiError extends Error {
  status: number;
  isAuthError: boolean;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.isAuthError = status === 401;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  headers?: Record<string, string>;
  body?: any;
  requiresAuth?: boolean;
  isFormData?: boolean;
  isMultipartFormData?: boolean;
  bypassCache?: boolean; // If true, skip cache check and force fresh API call
}

/**
 * Checks if the response is an ngrok security page
 */
const isNgrokSecurityPage = (text: string): boolean => {
  return (
    text.includes("ngrok.com") &&
    (text.includes("ERR_NGROK_6024") ||
      text.includes("Tunnel") ||
      text.includes("ngrok Error"))
  );
};

/**
 * Validates the stored auth token
 */
const isValidToken = (token: string): boolean => {
  try {
    // Basic JWT structure validation
    const parts = token.split(".");
    if (parts.length !== 3) return false;

    // Check if token is expired
    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      console.warn("Auth token is expired");
      // Dispatch event to trigger session expired dialog
      window.dispatchEvent(new CustomEvent('session-expired'));
      localStorage.removeItem(AUTH_TOKEN_KEY);
      return false;
    }

    return true;
  } catch (e) {
    console.warn("Invalid token format:", e);
    return false;
  }
};

/**
 * Handles API requests with appropriate authentication
 * @param endpoint The API endpoint to call (without base URL)
 * @param options Request options including method, headers, body
 * @returns The response data
 */
export const apiRequest = async <T>(
  endpoint: string,
  options: RequestOptions = {},
  retryCount = 0,
  redirectCount = 0
): Promise<T> => {
  const MAX_RETRIES = 3;
  const MAX_REDIRECTS = 5;
  const RETRY_DELAY = 1000; // 1 second
  const REQUEST_TIMEOUT = 15000; // 15 second timeout

  // Create a unique key for this request to track duplicates
  // Only include the request path (without query parameters) in the key for GET requests
  // This avoids treating pagination requests as the same request
  const method = options.method || "GET";
  const isGetRequest = method === "GET";

  // Create a more robust key for duplicate detection
  // For GET requests: path+query params (no timestamp params)
  // For mutations: method+path+body
  let requestKey;
  let cacheKey = "";

  if (isGetRequest) {
    // Extract path and query params
    const [path, queryString] = endpoint.split("?");

    // Create a cache key that excludes timestamps and cache busters
    if (queryString) {
      // Filter out _t and timestamp parameters for the cache key
      const cleanedParams = queryString
        .split("&")
        .filter(
          (param) =>
            !param.startsWith("_t=") &&
            !param.startsWith("_=") &&
            !param.startsWith("timestamp=")
        )
        .join("&");

      cacheKey = `GET-${path}${cleanedParams ? "?" + cleanedParams : ""}`;

      // For debounce key, include uniqueness factors
      const timestampParam = queryString
        .split("&")
        .find((param) => param.startsWith("_=") || param.startsWith("_t="));
      requestKey = `GET-${path}-${timestampParam || Date.now()}`;
    } else {
      cacheKey = `GET-${path}`;
      requestKey = `GET-${path}-${Date.now()}`;
    }

    // Check cache for GET requests when not retrying and cache is not bypassed
    if (retryCount === 0 && cacheKey && !options.bypassCache) {
      const cachedResponse = responseCache.get(cacheKey);

      // Determine cache TTL based on endpoint
      const ttl = cacheKey.includes("/users") ? USERS_CACHE_TTL : CACHE_TTL;

      if (cachedResponse && Date.now() - cachedResponse.timestamp < ttl) {
        // console.log(`Cache hit for ${cacheKey}`, cachedResponse.data);
        return cachedResponse.data as T;
      } else if (cachedResponse) {
        console.log(`Cache expired for ${cacheKey}, fetching fresh data`);
        responseCache.delete(cacheKey);
      }
    } else if (options.bypassCache && cacheKey) {
      // If bypassing cache, clear any existing cache entry to ensure fresh data
      responseCache.delete(cacheKey);
      console.log(`Bypassing cache for ${cacheKey}, forcing fresh API call`);
    }
  } else {
    // For non-GET requests, include method, full path, and body JSON
    const bodyString = options.body
      ? typeof options.body === "string"
        ? options.body
        : JSON.stringify(options.body)
      : "";
    requestKey = `${method}-${endpoint}-${bodyString}`;
  }

  // Create a base endpoint key for tracking request counts (exclude query params and timestamps)
  const baseEndpointKey = isGetRequest
    ? `GET-${endpoint.split("?")[0]}`
    : `${method}-${endpoint.split("?")[0]}`;

  // Increment the request count for this endpoint
  const currentCount = requestCountByEndpoint.get(baseEndpointKey) || 0;
  requestCountByEndpoint.set(baseEndpointKey, currentCount + 1);

  // Check if this endpoint is experiencing an infinite loop
  if (currentCount >= MAX_REQUESTS_PER_ENDPOINT) {
    console.warn(
      `Potential infinite loop detected for endpoint ${baseEndpointKey}, blocking request`
    );
    throw new ApiError(
      `Too many requests to ${baseEndpointKey} in a short time period. Please try again later.`,
      429
    );
  }

  // Special handling for supplier and invoice endpoints which seem to have more issues with infinite loops
  if ((baseEndpointKey.includes("/suppliers") || baseEndpointKey.includes("/invoices")) && isGetRequest) {
    // Set a lower threshold for these endpoints
    const SPECIAL_MAX_REQUESTS = 8; // Less strict for these endpoints

    if (currentCount >= SPECIAL_MAX_REQUESTS) {
      console.warn(
        `Potential infinite loop detected for ${baseEndpointKey}, blocking request (special threshold)`
      );
      throw new ApiError(
        `Too many requests to ${baseEndpointKey} in a short time period. Please try again later.`,
        429
      );
    }
  }

  // Debounce duplicate requests
  const now = Date.now();
  const lastRequestTime = recentRequests.get(requestKey);

  if (lastRequestTime && now - lastRequestTime < DEBOUNCE_TIME) {
    console.log(`Debouncing duplicate request: ${requestKey}`);
    // Wait for the debounce time to pass, then retry
    await new Promise((resolve) => setTimeout(resolve, DEBOUNCE_TIME));
  }

  // Update the last request time
  recentRequests.set(requestKey, now);

  // Clean up old entries from recentRequests to prevent memory leaks
  if (recentRequests.size > 100) {
    const cutoffTime = now - DEBOUNCE_TIME * 2;
    // Convert Map entries to array for better TypeScript compatibility
    const entries = Array.from(recentRequests.entries());
    for (const [key, timestamp] of entries) {
      if (timestamp < cutoffTime) {
        recentRequests.delete(key);
      }
    }
  }

  const url = `${API_URL}${endpoint}`;
  console.log(`Making ${method} request to: ${url}`);

  // Prepare headers
  const headers: Record<string, string> = {
    "Accept": "application/json",
    "Cache-Control": "no-cache",
    "Pragma": "no-cache",
    ...options.headers,
  };

  // Add authentication if required (default is true)
  if (options.requiresAuth !== false) {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token && isValidToken(token)) {
      headers["Authorization"] = `Bearer ${token}`;
    } else {
      // Only throw auth error if auth is explicitly required
      throw new ApiError("Authentication required", 401);
    }
  }

  // Handle different content types
  let body = options.body;
  if (options.isMultipartFormData) {
    // Don't set Content-Type for FormData, let the browser set it with boundary
    if (!(body instanceof FormData)) {
      throw new Error("Body must be FormData when isMultipartFormData is true");
    }
  } else if (options.isFormData) {
    headers["Content-Type"] = "application/x-www-form-urlencoded";
    if (typeof body === "object" && body !== null) {
      body = new URLSearchParams(body).toString();
    }
  } else if (body && typeof body === "object") {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(body);
  }

  // Create AbortController for timeout handling
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT);

  try {
    const fetchOptions: RequestInit = {
      method,
      headers,
      body,
      signal: controller.signal,
      // Add additional options for better cross-platform compatibility
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-cache'
    };

    const response = await fetch(url, fetchOptions);
    clearTimeout(timeoutId);

    // Handle redirects
    if (response.status >= 300 && response.status < 400) {
      if (redirectCount >= MAX_REDIRECTS) {
        throw new ApiError("Too many redirects", response.status);
      }

      const location = response.headers.get("Location");
      if (location) {
        console.log(`Redirecting to: ${location}`);
        return apiRequest<T>(location, options, retryCount, redirectCount + 1);
      }
    }

    // Handle different response types
    let responseData: any;
    const contentType = response.headers.get("content-type");

    if (contentType?.includes("application/json")) {
      const text = await response.text();
      
      // Check for ngrok security page
      if (isNgrokSecurityPage(text)) {
        throw new ApiError(
          "ngrok security page detected. Please visit the URL in your browser first.",
          502
        );
      }

      try {
        responseData = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error("Failed to parse JSON response:", text);
        throw new ApiError("Invalid JSON response from server", response.status);
      }
    } else if (contentType?.includes("text/")) {
      responseData = await response.text();
    } else {
      // For binary data or unknown content types
      responseData = await response.blob();
    }

    // Handle HTTP errors
    if (!response.ok) {
      let errorMessage = "An unexpected error occurred";
      
      try {
        // Try to parse error response for detailed error information
        const errorData = responseData;
        
        // Handle specific error structures
        if (errorData) {
          errorMessage = parseErrorMessage(errorData, errorMessage);
          if (errorData.detail && Array.isArray(errorData.detail) && !errorMessage.startsWith('Validation errors:')) {
            errorMessage = `Validation errors: ${errorMessage}`;
          }
        } else {
          // Fallback to HTTP status text
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
      } catch (parseError) {
        // If we can't parse the error response, use HTTP status
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      
      // Handle specific error codes with user-friendly messages
      if (response.status === 400) {
        // Keep the specific error message from the server for 400 errors
        // as they often contain validation details
      } else if (response.status === 401) {
        // Check if token is expired
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (token) {
          try {
            const parts = token.split(".");
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              if (payload.exp && payload.exp * 1000 < Date.now()) {
                // Token is expired - dispatch event to trigger session expired dialog
                window.dispatchEvent(new CustomEvent('session-expired'));
              }
            }
          } catch (e) {
            // If we can't parse token, treat as expired
            window.dispatchEvent(new CustomEvent('session-expired'));
          }
        }
        // Clear invalid token
        localStorage.removeItem(AUTH_TOKEN_KEY);
        throw new ApiError("Authentication failed. Please log in again.", 401);
      } else if (response.status === 403) {
        throw new ApiError("Access denied. You don't have permission to perform this action.", 403);
      } else if (response.status === 404) {
        throw new ApiError("Resource not found.", 404);
      } else if (response.status === 409) {
        // Conflict errors (usually constraint violations)
        throw new ApiError(errorMessage || "A conflict occurred. The data you're trying to save conflicts with existing records.", 409);
      } else if (response.status === 422) {
        // Validation errors
        throw new ApiError(errorMessage || "Validation failed. Please check your input data.", 422);
      } else if (response.status >= 500) {
        // Enhanced server error handling
        let serverErrorMessage = "Server error occurred. ";
        
        if (response.status === 500) {
          serverErrorMessage += "An internal server error occurred. ";
        } else if (response.status === 502) {
          serverErrorMessage += "Bad gateway - the server received an invalid response. ";
        } else if (response.status === 503) {
          serverErrorMessage += "Service unavailable - the server is temporarily overloaded. ";
        } else if (response.status === 504) {
          serverErrorMessage += "Gateway timeout - the server took too long to respond. ";
        } else {
          serverErrorMessage += `Server returned error ${response.status}. `;
        }
        
        // If we have a specific error message from the server, include it
        if (errorMessage && errorMessage !== "An unexpected error occurred" && errorMessage !== `HTTP ${response.status}: ${response.statusText}`) {
          serverErrorMessage += `Details: ${errorMessage}`;
        } else {
          serverErrorMessage += "Please try again later or contact support if the problem persists.";
        }
        
        throw new ApiError(serverErrorMessage, response.status);
      }
      
      // For all other errors, use the parsed error message
      throw new ApiError(errorMessage, response.status);
    }

    // Cache successful GET responses
    if (isGetRequest && cacheKey && retryCount === 0) {
      responseCache.set(cacheKey, {
        data: responseData,
        timestamp: Date.now(),
      });
      console.log(`Cached response for ${cacheKey}`);
    }

    return responseData as T;

  } catch (error: any) {
    clearTimeout(timeoutId);

    // Handle fetch-specific errors
    if (error.name === 'AbortError') {
      console.warn(`Request timeout for ${url}`);
      
      // Retry on timeout if we haven't exceeded max retries
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying request (${retryCount + 1}/${MAX_RETRIES}) after timeout: ${url}`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
        return apiRequest<T>(endpoint, options, retryCount + 1, redirectCount);
      } else {
        throw new ApiError("The request timed out. This might be due to a slow network connection or server issues. Please check your internet connection and try again.", 408);
      }
    } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.warn(`Network error for ${url}:`, error.message);
      
      // Retry on network error if we haven't exceeded max retries
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying request (${retryCount + 1}/${MAX_RETRIES}) after network error: ${url}`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
        return apiRequest<T>(endpoint, options, retryCount + 1, redirectCount);
      } else {
        // Provide more specific error messages based on the error
        let networkErrorMessage = "Unable to connect to the server. ";
        
        if (error.message.includes('Failed to fetch')) {
          networkErrorMessage += "This could be due to:\n" +
            "• Server is temporarily unavailable\n" +
            "• Network connection issues\n" +
            "• Firewall or proxy blocking the request\n" +
            "\nPlease check your internet connection and try again in a few moments.";
        } else if (error.message.includes('NetworkError')) {
          networkErrorMessage += "There was a network error. Please check your internet connection and try again.";
        } else if (error.message.includes('CORS')) {
          networkErrorMessage += "Cross-origin request blocked. Please contact the system administrator.";
        } else {
          networkErrorMessage += `Network error: ${error.message}. Please check your connection and try again.`;
        }
        
        throw new ApiError(networkErrorMessage, 0);
      }
    }

    // If it's already an ApiError, just re-throw it
    if (error instanceof ApiError) {
      throw error;
    }

    // For other errors, wrap them in ApiError with more descriptive messages
    console.error(`API request failed for ${url}:`, error);
    
    let genericErrorMessage = "An unexpected error occurred";
    if (error.message) {
      if (error.message.includes('JSON')) {
        genericErrorMessage = "Server returned an invalid response. Please try again.";
      } else if (error.message.includes('abort')) {
        genericErrorMessage = "Request was cancelled. Please try again.";
      } else {
        genericErrorMessage = `Request failed: ${error.message}`;
      }
    }
    
    throw new ApiError(genericErrorMessage, 500);
  }
};

/**
 * Clear cache entries by pattern or clear all cache
 * @param pattern Optional pattern to match cache keys for selective clearing
 */
export const clearApiCache = (pattern?: string): void => {
  if (pattern) {
    // Clear cache entries that match the pattern
    const keysToDelete: string[] = [];
    responseCache.forEach((value, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => responseCache.delete(key));
    console.log(`Cleared ${keysToDelete.length} cache entries matching pattern: ${pattern}`);
  } else {
    // Clear all cache
    responseCache.clear();
    console.log('Cleared all API cache');
  }
};

/**
 * Get cache statistics for debugging
 */
export const getCacheStats = (): { size: number; keys: string[] } => {
  return {
    size: responseCache.size,
    keys: Array.from(responseCache.keys())
  };
};

const apiClient = {
  get: <T>(
    endpoint: string,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ) => apiRequest<T>(endpoint, { ...options, method: "GET" }),
  post: <T>(
    endpoint: string,
    data: any,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ) => apiRequest<T>(endpoint, { ...options, method: "POST", body: data }),
  postForm: <T>(
    endpoint: string,
    data: any,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: "POST",
      body: data,
      isFormData: true,
    }),
  postMultipartForm: <T>(
    endpoint: string,
    formData: FormData,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: "POST",
      body: formData,
      isMultipartFormData: true,
    }),
  put: <T>(
    endpoint: string,
    data: any,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ) => apiRequest<T>(endpoint, { ...options, method: "PUT", body: data }),
  delete: <T>(
    endpoint: string,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ) => apiRequest<T>(endpoint, { ...options, method: "DELETE" }),
  patch: <T>(
    endpoint: string,
    data: any,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ) => apiRequest<T>(endpoint, { ...options, method: "PATCH", body: data }),
  apiRequest, // Expose the base request function if needed
  clearCache: clearApiCache, // Expose cache clearing function
  getCacheStats, // Expose cache stats for debugging
};

export default apiClient;
